/* Console — the entry point.
 *
 * This file is what index.html loads, and it is the successor to the forty
 * <script> tags that used to be in it. Those tags were not a list, they were an
 * order: prefs before theme so the theme is applied before the first paint,
 * the scheduling plugins before the store that reads their slot, every
 * component before the shell that mounts it. That order was load-bearing and it
 * lived in markup, where nothing could say why.
 *
 * It lives here now. Most of it is enforced by the imports themselves — a
 * module cannot be evaluated before something it imports — so what remains
 * below is only the part no import expresses: the files that register
 * themselves into a registry and are therefore imported for their side effect
 * alone. Those are grouped and named, because "this import looks unused" is
 * exactly the edit that would break the board.
 */
import { createApp } from 'vue';

import { App } from './app.js';
import { pluginSettings } from './plugins.js';
import { store } from './store.js';
import { ClientNote, NoteWarn } from './components/client-note.js';

/* Bundled with the rest, like Vue, so the console keeps its look offline. */
import '../css/refresh-a.css';

/* Side-effect imports. Each of these registers itself with something and
 * exports nothing anybody reads.
 *
 * Scheduling plugins register into the `scheduling` slot. The store reads that
 * slot as it is evaluated, so they have to be in place first — which the import
 * order here guarantees, since `./store.js` is only reached through the modules
 * below it. */
import './plugins/mangomint.js';

/* The Turn Tracker registers itself as a module in the apps registry, and pulls
 * in every component it is built from. Without this the switcher would offer
 * nothing but the two planned placeholders. */
import './components/turn-tracker.js';

/* DreamCloud registers itself the same way. The switcher lists it only for a
 * salon whose licence includes `dreamcloud`; the import is what puts it in the
 * registry at all, so it looks unused and is not. */
import './components/dreamcloud.js';

/* So does the Optimizer, for a salon whose licence includes `optimizer`. */
import './components/optimizer.js';

/* Which screen this is, before the first paint and for the same reason the
 * theme is read before it: the phone layout is a different shape, not a tint,
 * and coming up as a desktop board for one frame is a bottom bar sliding in
 * under a board that has already been laid out without it. Also puts `is-phone`
 * on <html>, which is what the stylesheet keys off — see store.setPhone. */
// Refresh A (DESIGN.md) layers css/refresh-a.css over dash.css.
// Every rule in it is scoped to `html.refresh-a`, so this class is its switch:
// drop the line and the console is back to dash.css alone.
document.documentElement.classList.add('refresh-a');

store.watchViewport();

const app = createApp(App);

/* A plugin can bring its own settings form, named on its registration. Those
 * components are registered globally because the settings page renders them by
 * name — it has no business importing a form for a system it does not know
 * exists. Adding a plugin therefore needs no change here. */
Object.keys(pluginSettings).forEach(function (name) {
  app.component(name, pluginSettings[name]);
});

/* The note icon beside a client's name, and the warning beside an offer to
 * hand them to a technician their notes name — in a dozen components, so
 * registered once here. See components/client-note.js. */
app.component('ClientNote', ClientNote);
app.component('NoteWarn', NoteWarn);

app.mount('#app');
